/**
 * /advertise — public landing for the Social Pulse® Ad Network.
 *
 * Pitch page for brands / agencies who want to run paid campaigns
 * across the 42-sister network. Shows the four placements, live
 * house-ad examples rendered from the real `/api/ads/serve` SDK,
 * and a request-rate-card form that drops a lead into the Hub.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Megaphone, Rocket, Globe2, ShieldCheck, Zap, Send,
  TrendingUp, Target, CheckCircle2,
} from "lucide-react";
import SocialPulseHeader from "@/components/socialpulse/SocialPulseHeader";
import SocialPulseFooter from "@/components/socialpulse/SocialPulseFooter";
import { http } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Four core slot families — maps to backend `PLACEMENTS` catalog.
const SLOT_SHOWCASE = [
  { code: "A1", name: "Top Leaderboard", desc: "Above-the-fold banner · 728×90 desktop · 320×50 mobile",   demo: "kerala" },
  { code: "A2", name: "Medium Rectangle", desc: "In-article unit · 300×250 · universal",                   demo: "foodpulse", narrow: true },
  { code: "N1", name: "Native Inline Card", desc: "Blends with feed · adapts to light/dark host",          demo: "travelerpulse" },
  { code: "H1", name: "Hero Sponsor",       desc: "Full-bleed takeover · 970×250 desktop · 320×180 mobile", demo: "hotelspulse" },
];

const BENEFITS = [
  { icon: Globe2,     title: "42 sister portals, one buy", body: "Reach travel, food, education, cinema, health, family-tree, local & more — all under the SocialPulse® umbrella. No fragmentation." },
  { icon: Target,     title: "Topic + geo targeting",      body: "Books only on travel sisters, or only in Kerala, or every sister in India — the network knows what each portal is for." },
  { icon: ShieldCheck,title: "Brand-safe by default",      body: "Curated editorial network. No UGC-rot, no clickbait inventory. Every impression lives next to community stories." },
  { icon: Zap,        title: "Universal snippet, 300ms",   body: "A single `<script>` tag powers every slot across every sister. Hard 300ms latency budget — never slows a page." },
  { icon: TrendingUp, title: "Live impression + click",    body: "Signed measurement tokens, 24h rolling counters, dashboard-ready events — no third-party pixel needed." },
  { icon: Rocket,     title: "Launch in hours, not weeks", body: "Send a creative, pick sisters and dates, go live. Priority queue over house cross-promos until your flight ends." },
];

export default function AdvertisePage() {
  const containerRef = useRef(null);

  // Form state (lead-capture, dropped into Mongo `advertise_leads`).
  const [form, setForm] = useState({
    company: "", name: "", email: "", website: "", budget: "", goals: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Load the Hub's universal ad SDK and let it fill the demo slots.
  // We rely on the SDK's own DOMContentLoaded + manual `SPAds.refresh()`
  // hook so it works both on first paint and after client-side nav.
  useEffect(() => {
    const EXISTING = document.querySelector("script[data-sp-ads-sdk]");
    const trigger = () => { try { window.SPAds && window.SPAds.refresh(); } catch (_) {} };
    if (EXISTING) {
      setTimeout(trigger, 50);
      return;
    }
    const s = document.createElement("script");
    s.src = `${BACKEND}/api/ads/sdk/v1.js`;
    s.async = true;
    s.setAttribute("data-sp-origin", BACKEND);
    s.setAttribute("data-sp-ads-sdk", "1");
    s.onload = trigger;
    document.body.appendChild(s);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.email) {
      toast.error("Company and email are required");
      return;
    }
    setSubmitting(true);
    try {
      await http.post("/ads/leads", form);
      setDone(true);
      toast.success("Got it — we'll be in touch within one business day.");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="advertise-page">
      <SocialPulseHeader />
      <main ref={containerRef} className="flex-1">
        {/* ── Hero ────────────────────────────────────────────── */}
        <section className="bg-gradient-to-b from-[#F5F9FE] via-white to-white border-b border-slate-100">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full mb-5">
                <Megaphone className="w-3.5 h-3.5" /> Social Pulse® Ad Network
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 leading-[1.05]">
                Advertise on <span className="text-blue-600">42 sister portals</span>, with one script.
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
                Reach engaged, geo-aware communities across travel, food, education,
                cinema, health, civic, family and more — all under the SocialPulse®
                umbrella. Brand-safe, native-friendly, measured in real time.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a
                  href="#request"
                  data-testid="advertise-cta-primary"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-sm transition-colors"
                >
                  Request a rate card <Send className="w-4 h-4" />
                </a>
                <a
                  href="#formats"
                  className="inline-flex items-center gap-2 h-12 px-5 rounded-full ring-1 ring-slate-200 hover:bg-slate-50 text-slate-700 font-semibold"
                >
                  See the formats
                </a>
              </div>
              <div className="mt-6 text-xs text-slate-500 flex flex-wrap gap-x-5 gap-y-1">
                <span>· 42 portals</span>
                <span>· 168 slots</span>
                <span>· 300ms SLA</span>
                <span>· Signed events</span>
              </div>
            </div>

            {/* Live hero preview — real SDK call to /api/ads/serve */}
            <div className="rounded-2xl bg-white ring-1 ring-slate-200 shadow-xl p-6" data-testid="advertise-hero-demo">
              <div className="text-[11px] font-bold tracking-widest text-slate-400 uppercase mb-3">
                Live preview · rendering through the real SDK
              </div>
              <div data-sp-portal="kerala" data-sp-slot="H1"></div>
              <div className="mt-5" data-sp-portal="hotelspulse" data-sp-slot="A2" style={{ maxWidth: 360 }}></div>
              <div className="mt-4 text-[11px] text-slate-400">
                Served from <code className="font-mono">/api/ads/serve</code>. Rotates every 15s.
              </div>
            </div>
          </div>
        </section>

        {/* ── Benefits ────────────────────────────────────────── */}
        <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16">
          <div className="mb-10">
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900">Why the network beats a single site</h2>
            <p className="mt-3 text-slate-600 max-w-2xl">
              One contract. One snippet. One dashboard. Every portal in the family,
              every placement, with native measurement baked in.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.title}
                  data-testid={`advertise-benefit-${b.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="rounded-2xl bg-slate-50 ring-1 ring-slate-100 p-6 hover:ring-blue-200 hover:bg-white transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="font-bold text-slate-900 text-lg">{b.title}</div>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.body}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Formats + live samples ─────────────────────────── */}
        <section id="formats" className="bg-slate-50 border-y border-slate-100 py-16">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
            <div className="mb-10">
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900">Four slot families</h2>
              <p className="mt-3 text-slate-600 max-w-2xl">
                Every sister is pre-wired for all four. Pick what fits your creative —
                we'll fit everything else automatically.
              </p>
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
              {SLOT_SHOWCASE.map((s) => (
                <div
                  key={s.code}
                  data-testid={`advertise-slot-${s.code}`}
                  className="rounded-2xl bg-white ring-1 ring-slate-200 p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-xs font-bold tracking-widest text-blue-600 uppercase">Slot · {s.code}</div>
                      <div className="text-xl font-black text-slate-900 mt-1">{s.name}</div>
                      <div className="text-xs text-slate-500 mt-1">{s.desc}</div>
                    </div>
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                  </div>
                  <div
                    data-sp-portal={s.demo}
                    data-sp-slot={s.code}
                    style={s.narrow ? { maxWidth: 320 } : undefined}
                  ></div>
                </div>
              ))}
            </div>
            <div className="mt-6 text-xs text-slate-500 text-center">
              Previews above are live — rotated every 15 seconds by the same decision endpoint sisters call.
            </div>
          </div>
        </section>

        {/* ── Lead form ───────────────────────────────────────── */}
        <section id="request" className="py-16">
          <div className="max-w-[760px] mx-auto px-6">
            <div className="rounded-3xl bg-slate-900 text-white p-8 sm:p-10 shadow-xl">
              <div className="flex items-center gap-2 text-xs font-bold tracking-widest text-blue-300 uppercase">
                <Megaphone className="w-3.5 h-3.5" /> Request a rate card
              </div>
              <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">Let's run your first flight</h2>
              <p className="mt-3 text-slate-300">
                Tell us a bit about the campaign and we'll come back with sister
                options, pricing, and an available start date within one business day.
              </p>

              {done ? (
                <div
                  data-testid="advertise-form-done"
                  className="mt-8 rounded-2xl bg-emerald-500/15 ring-1 ring-emerald-400/40 p-6 text-center"
                >
                  <CheckCircle2 className="w-10 h-10 text-emerald-300 mx-auto" />
                  <div className="mt-3 text-lg font-bold">Thanks — your request is in.</div>
                  <div className="mt-1 text-sm text-slate-300">
                    We'll reply to <span className="font-semibold">{form.email}</span> shortly.
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  data-testid="advertise-lead-form"
                  className="mt-7 grid sm:grid-cols-2 gap-4"
                >
                  <input
                    required
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    placeholder="Company"
                    data-testid="advertise-form-company"
                    className="h-12 px-4 rounded-xl bg-slate-800 ring-1 ring-slate-700 focus:ring-blue-400 outline-none placeholder:text-slate-500"
                  />
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name"
                    data-testid="advertise-form-name"
                    className="h-12 px-4 rounded-xl bg-slate-800 ring-1 ring-slate-700 focus:ring-blue-400 outline-none placeholder:text-slate-500"
                  />
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Work email"
                    data-testid="advertise-form-email"
                    className="h-12 px-4 rounded-xl bg-slate-800 ring-1 ring-slate-700 focus:ring-blue-400 outline-none placeholder:text-slate-500"
                  />
                  <input
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                    placeholder="Website"
                    data-testid="advertise-form-website"
                    className="h-12 px-4 rounded-xl bg-slate-800 ring-1 ring-slate-700 focus:ring-blue-400 outline-none placeholder:text-slate-500"
                  />
                  <select
                    value={form.budget}
                    onChange={(e) => setForm({ ...form, budget: e.target.value })}
                    data-testid="advertise-form-budget"
                    className="h-12 px-4 rounded-xl bg-slate-800 ring-1 ring-slate-700 focus:ring-blue-400 outline-none sm:col-span-2 text-slate-200"
                  >
                    <option value="">Monthly budget (optional)</option>
                    <option value="&lt;1k">Under $1,000</option>
                    <option value="1-5k">$1,000 – $5,000</option>
                    <option value="5-25k">$5,000 – $25,000</option>
                    <option value="25k+">$25,000+</option>
                  </select>
                  <textarea
                    value={form.goals}
                    onChange={(e) => setForm({ ...form, goals: e.target.value })}
                    placeholder="What are you promoting? Any sisters or geos in mind?"
                    data-testid="advertise-form-goals"
                    rows={4}
                    className="px-4 py-3 rounded-xl bg-slate-800 ring-1 ring-slate-700 focus:ring-blue-400 outline-none placeholder:text-slate-500 sm:col-span-2"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="advertise-form-submit"
                    className="sm:col-span-2 h-12 rounded-xl bg-blue-500 hover:bg-blue-400 text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {submitting ? "Sending…" : (<>Send request <Send className="w-4 h-4" /></>)}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
      <SocialPulseFooter />
    </div>
  );
}
